/**
 * AuraTemplatesScreen - Full page for browsing all templates
 *
 * Route: /aura/templates
 */
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, Input } from "@aix/ui"
import { Search, Sparkles, LayoutTemplate, ChevronLeft, ArrowRight } from "lucide-react"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"

// Template categories
const CATEGORIES = [
  { value: "all", label: "All Templates", icon: LayoutTemplate },
  { value: "landing", label: "Landing Pages" },
  { value: "pricing", label: "Pricing" },
  { value: "features", label: "Features" },
  { value: "about", label: "About" },
  { value: "contact", label: "Contact" },
  { value: "blog", label: "Blog" },
  { value: "portfolio", label: "Portfolio" },
  { value: "dashboard", label: "Dashboard" },
] as const

const AuraTemplatesScreen = () => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("all")
  const [usingTemplateId, setUsingTemplateId] = useState<string | null>(null)

  // Fetch templates
  const { data: templates, isLoading } = trpc.auraTemplates.listTemplates.useQuery({
    category: selectedCategory === "all" ? undefined : (selectedCategory as any),
    search: searchQuery || undefined,
    limit: 100,
  })

  // Fetch featured templates
  const { data: featuredTemplates } = trpc.auraTemplates.getFeaturedTemplates.useQuery(
    { limit: 6 },
    { enabled: selectedCategory === "all" && !searchQuery },
  )

  // Fetch categories with counts
  const { data: categoryCounts } = trpc.auraTemplates.getCategories.useQuery()

  // Use template mutation
  const useTemplateMutation = trpc.auraTemplates.useTemplate.useMutation({
    onSuccess: (data) => {
      toast.success("Project created from template!")
      navigate({ to: `/aura/editor/${data.project.id}` })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create project from template")
    },
    onSettled: () => {
      setUsingTemplateId(null)
    },
  })

  const handleUseTemplate = (templateId: string, templateName: string) => {
    setUsingTemplateId(templateId)
    useTemplateMutation.mutate({
      templateId,
      projectName: `${templateName} Project`,
    })
  }

  const getCategoryCount = (category: string) => {
    if (category === "all") {
      return categoryCounts?.reduce((acc, c) => acc + (c.count || 0), 0) || 0
    }
    return categoryCounts?.find((c) => c.category === category)?.count || 0
  }

  const showFeatured = selectedCategory === "all" && !searchQuery && featuredTemplates && featuredTemplates.length > 0

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/aura" })}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-primary" />
              <h1 className="text-xl font-semibold text-foreground">Templates</h1>
            </div>
          </div>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-56 border-r border-border bg-secondary/30 p-4 overflow-y-auto">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Categories</h3>
          <div className="space-y-1">
            {CATEGORIES.map((cat) => {
              const count = getCategoryCount(cat.value)
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedCategory === cat.value
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-secondary text-foreground"
                  }`}
                >
                  <span>{cat.label}</span>
                  <span
                    className={`text-xs ${
                      selectedCategory === cat.value ? "text-primary-foreground/70" : "text-muted-foreground"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : templates && templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <LayoutTemplate className="w-16 h-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">No templates found</h2>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? `No templates match "${searchQuery}"` : "No templates in this category yet"}
              </p>
              {searchQuery && (
                <Button variant="outline" onClick={() => setSearchQuery("")}>
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Featured Section */}
              {showFeatured && (
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Featured Templates
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {featuredTemplates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onUse={() => handleUseTemplate(template.id, template.name)}
                        isLoading={usingTemplateId === template.id}
                        disabled={usingTemplateId !== null}
                        featured
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* All Templates */}
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  {selectedCategory === "all"
                    ? "All Templates"
                    : `${CATEGORIES.find((c) => c.value === selectedCategory)?.label || ""}`}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {templates?.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onUse={() => handleUseTemplate(template.id, template.name)}
                      isLoading={usingTemplateId === template.id}
                      disabled={usingTemplateId !== null}
                    />
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Template Card Component
interface TemplateCardProps {
  template: {
    id: string
    name: string
    slug: string
    description: string | null
    category: string
    tags: string[] | null
    thumbnail_url: string | null
    usage_count: number | null
    is_featured: boolean | null
  }
  onUse: () => void
  isLoading: boolean
  disabled: boolean
  featured?: boolean
}

const TemplateCard = ({ template, onUse, isLoading, disabled, featured }: TemplateCardProps) => {
  return (
    <div
      className={`group border rounded-lg overflow-hidden bg-background hover:border-primary hover:shadow-lg transition-all ${
        featured ? "ring-2 ring-primary/20" : ""
      }`}
    >
      {/* Thumbnail */}
      <div className="aspect-[4/3] bg-secondary relative overflow-hidden">
        {template.thumbnail_url ? (
          <img
            src={template.thumbnail_url}
            alt={template.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary to-secondary/50">
            <LayoutTemplate className="w-12 h-12 text-muted-foreground/30" />
          </div>
        )}
        {/* Featured badge */}
        {template.is_featured && (
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 text-xs rounded-full bg-primary text-primary-foreground flex items-center gap-1 shadow-md">
              <Sparkles className="w-3 h-3" />
              Featured
            </span>
          </div>
        )}
        {/* Category badge */}
        <div className="absolute top-3 right-3">
          <span className="px-2.5 py-1 text-xs rounded-full bg-background/90 text-foreground capitalize shadow-sm">
            {template.category}
          </span>
        </div>
        {/* Hover overlay with button */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Button
            onClick={onUse}
            disabled={disabled}
            className="transform translate-y-4 group-hover:translate-y-0 transition-transform"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                Use Template
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h4 className="font-semibold text-foreground truncate">{template.name}</h4>
        {template.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{template.description}</p>
        )}
        <div className="flex items-center gap-3 mt-3">
          <span className="text-xs text-muted-foreground">{template.usage_count || 0} uses</span>
          {template.tags && template.tags.length > 0 && (
            <div className="flex gap-1">
              {template.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuraTemplatesScreen
